import type {
  Address,
  AdminAccount,
  Appointment,
  AppUser,
  Article,
  City,
  Company,
  Country,
  FlaggedReview,
  Formula,
  NotificationTemplates,
  Offer,
  OfferTransaction,
  PregnancyWeek,
  ReminderSettings,
} from "@domain/entities";
import type { RightsMatrix } from "@domain/value-objects";

/* Référentiels bruts repris du prototype de design. */

export const CITIES = [
  "Abidjan", "Bouaké", "Yamoussoukro", "San-Pédro", "Korhogo",
  "Dakar", "Cotonou", "Lomé", "Paris", "Lyon", "Marseille",
] as const;

const FIRST_NAMES = [
  "Awa", "Fatou", "Aïcha", "Mariam", "Adjoa", "Nadia", "Sarah", "Chloé", "Emma", "Léa",
  "Aminata", "Josiane", "Grace", "Aya", "Yasmine", "Coumba", "Kadiatou", "Salimata", "Marion", "Camille",
];

const LAST_NAMES = [
  "Koffi", "Traoré", "Bamba", "Diallo", "Kouassi", "Yao", "N'Guessan", "Kouadio",
  "Sy", "Ndiaye", "Fall", "Camara", "Dosso", "Ouattara", "Sanogo", "Konan",
];

const COMPANY_NAMES = [
  "Clinique Sainte-Marie", "Maternité Les Palmiers", "Pharmacie du Plateau", "Institut Bien-Naître",
  "Sage-femme à domicile — Awa K.", "Pharmacie Farah", "Clinique El-Rapha", "Boutique Petits Pas",
  "Centre de kinésithérapie Douceur", "Pharmacie de la Riviera", "Maternité Saint-Joseph", "Institut Mama Zen",
];

const COMPANY_TYPES = [
  "Clinique / Maternité", "Pharmacie", "Institut de bien-être",
  "Boutique bébé", "Sage-femme indépendante", "Kinésithérapeute",
];

const OFFER_TITLES = [
  "Pack naissance complet", "-15% sur consultation prénatale", "1 séance de kiné offerte",
  "Bilan grossesse à prix réduit", "Kit de bienvenue bébé", "Consultation sage-femme à domicile",
  "Réduction sur la layette", "Offre allaitement accompagné",
];

const PRICINGS = [
  "basic-30k", "standard-50k", "premium-75k", "enterprise-100k",
  "basic-30k", "standard-50k", "premium-75k", "enterprise-100k",
];

const PRICE_AMOUNTS = [30000, 50000, 75000, 100000];

const OFFER_CATS = [
  "Santé", "Bien-être", "Shopping", "Alimentation", "Sport", "Beauté", "Éducation", "Services",
];

const ARTICLE_TITLES = [
  "Bien vivre son premier trimestre", "5 aliments à privilégier pendant la grossesse",
  "Préparer sa valise de maternité", "Comprendre le suivi prénatal",
  "Le baby blues : en parler sans tabou", "Choisir sa sage-femme à Abidjan",
  "Sport et grossesse : ce qu'il faut savoir", "Allaitement : les bonnes postures",
];

const ARTICLE_CATS = ["Grossesse", "Santé", "Nutrition", "Bébé", "Bien-être"];

const ADDRESS_NAMES = [
  "Clinique de la Riviera", "Pharmacie Awa", "Restaurant Chez Fatou", "Boutique Ti Bébé",
  "Kiné Plateau Santé", "Maternité Espoir", "Institut Sérénité", "Pharmacie du Phare",
  "Café des Mamans", "Boutique Layette d'Or",
];

const ADDR_CATS = ["Maternité", "Pharmacie", "Restaurant", "Boutique bébé", "Kinésithérapeute", "Sage-femme"];

const REASONS = ["Contenu inapproprié", "Faux avis suspecté", "Propos injurieux", "Information erronée"];

const LONG_DESC =
  "Cette structure propose un accompagnement personnalisé pour les futures mères, avec un plateau technique " +
  "renouvelé en 2025 et une équipe pluridisciplinaire disponible du lundi au samedi. Les rendez-vous peuvent " +
  "être pris en ligne ou par téléphone, et un service de garde est assuré les jours fériés.";

const LONG_COMMENT =
  "J'ai été très déçue par mon passage ici, le personnel n'a pas du tout été à l'écoute et j'ai attendu plus " +
  "de deux heures sans explication. Je ne recommande pas du tout cette adresse à d'autres futures mamans.";

const DATES = [
  "12 juil. 2026", "03 juil. 2026", "28 juin 2026", "15 juin 2026",
  "02 juin 2026", "21 mai 2026", "09 mai 2026", "30 avr. 2026",
];

const FRUIT_NAMES = [
  "Graine de pavot", "Graine de sésame", "Lentille", "Myrtille", "Framboise", "Raisin", "Kumquat",
  "Fraise", "Prune", "Citron vert", "Pêche", "Pomme", "Avocat", "Grenade", "Poivron", "Tomate",
  "Banane", "Carotte", "Papaye", "Mangue", "Épi de maïs", "Chou-fleur", "Laitue", "Brocoli",
  "Aubergine", "Courge butternut", "Chou", "Noix de coco", "Jicama", "Ananas", "Cantaloup",
  "Melon miel", "Romaine", "Bette à carde", "Poireau", "Petite pastèque", "Pastèque",
];

/* ------------------------------- Générateurs ------------------------------ */

export const SEED_FORMULAS: Formula[] = [
  { id: "form0", slug: "repertoire-3m", label: "Référencement répertoire (3 mois)", description: null, amountXof: 30_000, durationDays: 90, sortOrder: 1, isActive: true },
  { id: "form1", slug: "pub-accueil-5", label: "Pub accueil (5 apparitions/mois)", description: null, amountXof: 75_000, durationDays: 30, sortOrder: 2, isActive: true },
  { id: "form2", slug: "pub-accueil-10", label: "Pub accueil (10 apparitions/mois)", description: null, amountXof: 100_000, durationDays: 30, sortOrder: 3, isActive: true },
];

const CONTACT_CHANNELS = ['call', 'whatsapp', 'email'] as const;

export function seedCompanies(): Company[] {
  return Array.from({ length: 34 }, (_, i): Company => {
    const status = i % 5 === 0 ? "rejected" : i % 3 === 0 ? "pending" : "approved";
    const suffix = i >= COMPANY_NAMES.length ? ` ${Math.floor(i / COMPANY_NAMES.length) + 1}` : "";
    const channel = CONTACT_CHANNELS[i % CONTACT_CHANNELS.length];
    return {
      id: `c${i}`,
      name: COMPANY_NAMES[i % COMPANY_NAMES.length] + suffix,
      type: COMPANY_TYPES[i % COMPANY_TYPES.length],
      city: CITIES[i % CITIES.length],
      email: `contact${i}@babimams-pro.com`,
      phone: null,
      ownerPhone: `+225 07 0${i % 9} 22 3${i % 9} 44`,
      contactChannel: channel,
      contactDestination: channel === 'email' ? `contact${i}@babimams-pro.com` : `+225 07 0${i % 9} 22 3${i % 9} 44`,
      status,
      submittedAt: DATES[i % DATES.length],
      approvedAt: status === 'approved' ? DATES[(i + 1) % DATES.length] : null,
      rejectedAt: status === 'rejected' ? DATES[(i + 1) % DATES.length] : null,
      rejectionReason: status === 'rejected' ? 'Informations insuffisantes' : null,
    };
  });
}

export function seedOffers(): Offer[] {
  return Array.from({ length: 40 }, (_, i): Offer => {
    const status = i % 6 === 0 ? "rejected" : i % 4 === 0 ? "pending" : "active";
    const publishedAt = status === "active" ? DATES[i % DATES.length] : null;
    const callClicks = (i * 13 + 3) % 200;
    const waClicks = (i * 7 + 5) % 150;
    const mailClicks = (i * 5 + 2) % 80;
    return {
      id: `o${i}`,
      title: OFFER_TITLES[i % OFFER_TITLES.length],
      company: COMPANY_NAMES[i % COMPANY_NAMES.length],
      ownerType: 'business',
      category: OFFER_CATS[i % OFFER_CATS.length],
      pricing: PRICINGS[i % PRICINGS.length],
      priceAmount: PRICE_AMOUNTS[i % PRICE_AMOUNTS.length],
      description: LONG_DESC,
      clicks: callClicks + waClicks + mailClicks,
      viewsCount: (i * 53 + 10) % 2000,
      callClicksCount: callClicks,
      whatsappClicksCount: waClicks,
      emailClicksCount: mailClicks,
      status,
      publishedAt,
      expiresAt: publishedAt ? DATES[(i + 2) % DATES.length] : null,
      createdAt: DATES[(i + 1) % DATES.length],
      photoUrls: [],
      photoKeys: [],
      isPaid: false,
    };
  });
}

export function seedArticles(): Article[] {
  return Array.from({ length: 22 }, (_, i): Article => ({
    id: `a${i}`,
    title: ARTICLE_TITLES[i % ARTICLE_TITLES.length] + (i >= ARTICLE_TITLES.length ? " (2)" : ""),
    excerpt: null,
    body: LONG_DESC,
    categoryId: null,
    categoryLabel: ARTICLE_CATS[i % ARTICLE_CATS.length],
    coverUrl: null,
    publishedAt: i % 3 === 0 ? null : DATES[i % DATES.length],
    isFeatured: false,
    imageUrls: [],
    imageKeys: [],
  }));
}

export function seedAddresses(): Address[] {
  return Array.from({ length: 24 }, (_, i): Address => {
    const suffix = i >= ADDRESS_NAMES.length ? ` ${Math.floor(i / ADDRESS_NAMES.length) + 1}` : "";
    const reviewsCount = (i % 5) + 1;
    const rating = Number((3.6 + ((i * 7) % 14) / 10).toFixed(1));
    return {
      id: `ad${i}`,
      name: ADDRESS_NAMES[i % ADDRESS_NAMES.length] + suffix,
      category: ADDR_CATS[i % ADDR_CATS.length],
      city: CITIES[i % CITIES.length],
      address: `${12 + i} Rue des Cocotiers, ${CITIES[i % CITIES.length]}`,
      rating,
      reviewsCount,
      publishedAt: i % 5 === 0 ? null : DATES[i % DATES.length],
    };
  });
}

export function seedAddressReviews(): Array<{ id: string; addressId: string; author: string; rating: number; comment: string; createdAt: string }> {
  const comments = [
    "Très bon accueil, personnel attentionné.",
    "Service rapide et efficace, je recommande.",
    "Propre et bien équipé, mais l'attente est longue.",
    "Personnel à l'écoute, prix raisonnables.",
    "Un peu cher mais qualité au rendez-vous.",
  ];
  const result: Array<{ id: string; addressId: string; author: string; rating: number; comment: string; createdAt: string }> = [];
  for (let i = 0; i < 24; i++) {
    const reviewsCount = (i % 5) + 1;
    for (let j = 0; j < reviewsCount; j++) {
      result.push({
        id: `rev_ad${i}_${j}`,
        addressId: `ad${i}`,
        author: `${FIRST_NAMES[(i + j) % FIRST_NAMES.length]} ${LAST_NAMES[(i * 2 + j) % LAST_NAMES.length]}`,
        rating: Math.max(1, Math.min(5, Math.round(3.6 + ((i * 7) % 14) / 10))),
        comment: comments[(i + j) % comments.length],
        createdAt: DATES[(i + j) % DATES.length],
      });
    }
  }
  return result;
}

export function seedReviews(): FlaggedReview[] {
  return Array.from({ length: 5 }, (_, i): FlaggedReview => ({
    id: `r${i}`,
    author: `${FIRST_NAMES[i % FIRST_NAMES.length]} ${LAST_NAMES[i % LAST_NAMES.length]}`,
    address: ADDRESS_NAMES[i % ADDRESS_NAMES.length],
    reason: REASONS[i % REASONS.length],
    comment: LONG_COMMENT,
  }));
}

const CITY_COUNTRY: Record<string, string> = {
  Abidjan: "+225", Bouaké: "+225", Yamoussoukro: "+225", "San-Pédro": "+225", Korhogo: "+225",
  Dakar: "+221", Cotonou: "+229", Lomé: "+228",
  Paris: "+33", Lyon: "+33", Marseille: "+33",
};

function seedDueDate(i: number): string {
  if (i % 9 === 0) {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  }
  if (i % 7 === 0) return "";
  const week = 12 + (i % 28);
  const d = new Date();
  d.setDate(d.getDate() + (40 - week) * 7);
  return d.toISOString().slice(0, 10);
}

export function seedAppUsers(): AppUser[] {
  return Array.from({ length: 52 }, (_, i): AppUser => {
    const first = FIRST_NAMES[i % FIRST_NAMES.length];
    const last = LAST_NAMES[(i * 3) % LAST_NAMES.length];
    const city = CITIES[i % CITIES.length];
    return {
      id: `u${i}`,
      name: `${first} ${last}`,
      initials: (first[0] + last[0]).toUpperCase(),
      city,
      countryCode: CITY_COUNTRY[city] ?? "",
      email: `${first.toLowerCase()}.${last.toLowerCase()}@mail.com`,
      phone: `+225 05 1${i % 9} 33 5${i % 9} 66`,
      avatarUrl: "",
      role: (["maman", "futur-parent", "papa", "grand-mere", "tante", "autre"] as const)[i % 6],
      dueDate: seedDueDate(i),
      babySex: i % 3 === 0 ? "garcon" : i % 3 === 1 ? "fille" : "",
      isFirstChild: i % 4 === 0 ? null : i % 2 === 0,
      age: i % 5 === 0 ? null : 20 + (i % 20),
      wantsWeeklyTips: i % 6 !== 0,
      wantsLocalNews: i % 8 !== 0,
      locale: i % 10 === 0 ? "en" : "fr",
      pregnancy:
        i % 9 === 0 ? "Post-partum" : i % 7 === 0 ? "Non renseigné" : `${12 + (i % 28)} SA`,
      status: i % 11 === 0 ? "disabled" : "active",
      registeredAt: DATES[i % DATES.length],
    };
  });
}

export function defaultRights(): RightsMatrix {
  return {
    companies: { create: false, edit: true, delete: false },
    offers: { create: true, edit: true, delete: false },
    articles: { create: true, edit: true, delete: false },
    addresses: { create: true, edit: true, delete: false },
    fruits: { create: false, edit: true, delete: false },
    performance: { create: false, edit: false, delete: false },
    formulas: { create: false, edit: false, delete: false },
    countries: { create: false, edit: false, delete: false },
    users: { create: false, edit: false, delete: false },
    admins: { create: false, edit: false, delete: false },
  };
}

export function seedAdmins(): AdminAccount[] {
  return [
    { id: "admin0", name: "Aïcha Koné", email: "aicha.kone@babimams.com", role: "SUPER_ADMIN", status: "active" },
    { id: "admin1", name: "Marc Dubois", email: "marc.dubois@babimams.com", role: "ADMIN", status: "active", rights: defaultRights() },
    { id: "admin2", name: "Fatou Diarra", email: "fatou.diarra@babimams.com", role: "EDITOR", status: "disabled", rights: defaultRights() },
  ];
}

export function seedCountries(): Country[] {
  return [
    { id: "ct0", name: "Côte d'Ivoire", code: "+225", digitLen: 10, example: "0701234567", providerFeePercent: 0, sortOrder: 1, isActive: true },
    { id: "ct1", name: "Sénégal", code: "+221", digitLen: 9, example: "701234567", providerFeePercent: 0, sortOrder: 2, isActive: true },
    { id: "ct2", name: "Bénin", code: "+229", digitLen: 8, example: "97123456", providerFeePercent: 0, sortOrder: 3, isActive: true },
    { id: "ct3", name: "Togo", code: "+228", digitLen: 8, example: "90123456", providerFeePercent: 0, sortOrder: 4, isActive: true },
    { id: "ct4", name: "France", code: "+33", digitLen: 9, example: "612345678", providerFeePercent: 0, sortOrder: 5, isActive: true },
    { id: "ct5", name: "Cameroun", code: "+237", digitLen: 9, example: "612345678", providerFeePercent: 0, sortOrder: 6, isActive: false },
  ];
}

export function seedCities(): City[] {
  return [
    { id: "city_abj", name: "Abidjan", countryCode: "+225", isActive: true },
    { id: "city_bke", name: "Bouaké", countryCode: "+225", isActive: true },
    { id: "city_yam", name: "Yamoussoukro", countryCode: "+225", isActive: true },
    { id: "city_san", name: "San-Pédro", countryCode: "+225", isActive: true },
    { id: "city_kor", name: "Korhogo", countryCode: "+225", isActive: true },
    { id: "city_dkr", name: "Dakar", countryCode: "+221", isActive: true },
    { id: "city_cot", name: "Cotonou", countryCode: "+229", isActive: true },
    { id: "city_lom", name: "Lomé", countryCode: "+228", isActive: true },
    { id: "city_par", name: "Paris", countryCode: "+33", isActive: true },
    { id: "city_lyo", name: "Lyon", countryCode: "+33", isActive: true },
    { id: "city_mrs", name: "Marseille", countryCode: "+33", isActive: true },
  ];
}

export function seedPregnancyWeeks(): PregnancyWeek[] {
  return FRUIT_NAMES.map((fruit, i) => ({
    week: i + 4,
    title: "",
    body: "",
    fruit,
    tailleCm: null,
    poidsG: null,
    moisIndex: null,
    anecdote: null,
    tipsToDo: [],
    tipsToAvoid: [],
    imageUrl: null,
    imageKey: null,
    illustrationSvg: null,
    illustrationScalePct: null,
    validated: false,
  }));
}

export function seedReminder(): ReminderSettings {
  return {
    enabled: true,
    daysBefore: 15,
    template:
      `Bonjour {entreprise}, votre formule "{formule}" arrive à échéance dans {jours} jours. ` +
      "Renouvelez pour continuer à apparaître sur BabiMams.",
  };
}

export function seedNotificationTemplates(): NotificationTemplates {
  return {
    offerApproved: {
      channel: "both",
      subject: "Votre offre a été approuvée",
      body: `Bonjour {entreprise}, votre offre "{offre}" est maintenant visible par les mamans sur BabiMams.`,
    },
    offerRejected: {
      channel: "both",
      subject: "Votre offre n'a pas été approuvée",
      body: "Bonjour {entreprise}, votre offre '{offre}' n'a pas été approuvée. Contactez-nous pour en savoir plus.",
    },
    articlePublished: {
      channel: "app",
      subject: "Nouvel article disponible",
      body: "Un nouvel article '{article}' vient d'être publié sur BabiMams.",
    },
  };
}

/** Journal des utilisations dérivé des offres actives et des comptes actifs. */
export function seedTransactions(users: readonly AppUser[], offers: readonly Offer[]): OfferTransaction[] {
  const activeOffers = offers.filter((o) => o.status === "active");
  const activeUsers = users.filter((u) => u.status === "active");
  if (activeOffers.length === 0 || activeUsers.length === 0) return [];

  return Array.from({ length: 60 }, (_, i): OfferTransaction => {
    const user = activeUsers[i % activeUsers.length];
    const offer = activeOffers[i % activeOffers.length];
    return {
      id: `tx${i}`,
      userId: user.id,
      userName: user.name,
      companyName: offer.company,
      offerTitle: offer.title,
      usedAt: DATES[i % DATES.length],
    };
  });
}

const APPOINTMENT_REASONS = [
  'Consultation prénatale', 'Échographie', 'Prise de sang', 'Suivi sage-femme',
  'Cours de préparation à l\'accouchement', 'Consultation pédiatre', 'Vaccination',
];
const APPOINTMENT_LOCATIONS = [
  'Clinique Sainte-Marie, Abidjan', 'Maternité Les Lilas, Paris', 'Hôpital général, Dakar',
  'Cabinet Dr. Koné, Abidjan', 'CHU de Cotonou',
];
const REMINDER_OFFSETS = ['15min', '30min', '1h', '2h', '1d'] as const;
const FUTURE_DATES = ['2026-09-20T09:00:00.000Z', '2026-09-25T14:30:00.000Z', '2026-10-02T10:00:00.000Z', '2026-10-10T08:00:00.000Z', '2026-11-05T11:00:00.000Z'];
const PAST_DATES = ['2026-07-15T09:00:00.000Z', '2026-08-01T14:00:00.000Z', '2026-08-20T10:30:00.000Z', '2026-09-01T08:00:00.000Z', '2026-09-05T15:00:00.000Z'];

export function seedAppointments(users: readonly AppUser[]): Appointment[] {
  const activeUsers = users.filter((u) => u.status === 'active');
  return Array.from({ length: 20 }, (_, i): Appointment => {
    const user = activeUsers[i % activeUsers.length];
    const isFuture = i % 3 !== 0;
    const datePool = isFuture ? FUTURE_DATES : PAST_DATES;
    return {
      id: `apt${i}`,
      userId: user.id,
      userName: user.name,
      userEmail: user.email || '',
      reason: APPOINTMENT_REASONS[i % APPOINTMENT_REASONS.length],
      scheduledAt: datePool[i % datePool.length],
      location: APPOINTMENT_LOCATIONS[i % APPOINTMENT_LOCATIONS.length],
      reminderOffset: REMINDER_OFFSETS[i % REMINDER_OFFSETS.length],
      createdAt: DATES[i % DATES.length],
    };
  });
}

export const SEED_IMPORT_PREVIEW = [
  { name: "Clinique Sainte-Marie", category: "Santé", city: "Abidjan", phone: "+225 07 01 02 03" },
  { name: "Pharmacie du Plateau", category: "Pharmacie", city: "Abidjan", phone: "+225 07 04 05 06" },
  { name: "Spa Bien-Naître", category: "Bien-être", city: "Dakar", phone: "+221 77 11 22 33" },
  { name: "Boutique Bébé Chéri", category: "Shopping", city: "Cotonou", phone: "+229 96 44 55 66" },
  { name: "Maternité Les Lilas", category: "Santé", city: "Paris", phone: "+33 6 12 34 56 78" },
];

export const HELP_SECTIONS = [
  {
    title: "Tableau de bord",
    body: "Page d'accueil du back-office. Les KPIs du haut (comptes en attente, offres actives, articles publiés, utilisatrices actives) donnent une vue instantanée de l'activité. La section 'Demandes en attente' liste les comptes entreprise et les offres qui attendent une décision : clique Approuver ou Rejeter — une confirmation est demandée avant chaque action. 'Offres les plus cliquées' montre le top des offres consultées par les mamans ce mois-ci.",
  },
  {
    title: "Comptes entreprise",
    body: "Liste de toutes les entreprises inscrites sur la plateforme. Filtre par statut (en attente / approuvé / rejeté / suspendu) ou cherche par nom. Clique une ligne pour ouvrir le détail complet (coordonnées, canal de contact, type d'entreprise, ville). Depuis le détail tu peux Approuver, Rejeter ou Suspendre le compte, ou cliquer Modifier pour corriger n'importe quel champ. Les actions Approuver, Rejeter, Suspendre demandent toutes une confirmation avant d'être exécutées.",
  },
  {
    title: "Offres",
    body: "Toutes les offres publiées ou soumises par les entreprises. Filtre par statut (en attente / active / rejetée / suspendue) ou cherche par titre. Ouvre une offre pour voir ses photos, son prix, sa description et son nombre de clics. Tu peux Approuver, Rejeter ou Suspendre une offre — chaque action demande une confirmation. La sélection multiple permet d'approuver ou rejeter plusieurs offres d'un coup via les boutons en haut de liste.",
  },
  {
    title: "Articles",
    body: "Contenu éditorial affiché dans l'application. Clique '+ Nouvel article', remplis le titre, choisis une catégorie, ajoute une image de couverture et saisis le texte. Un article reste en Brouillon jusqu'à ce que tu cliques Publier. Tu peux Dépublier un article actif pour le retirer sans le supprimer. La suppression est définitive et demande une confirmation.",
  },
  {
    title: "Suivi de grossesse",
    body: "Gestion des 37 semaines de grossesse affichées dans l'application. Chaque semaine est associée à un nom de fruit et une image. Clique Modifier sur une semaine pour changer le nom ou remplacer l'image, puis Valider pour enregistrer. Les modifications s'affichent immédiatement dans l'application mobile.",
  },
  {
    title: "Répertoire",
    body: "Deux onglets dans cet écran. 'Bonnes adresses' : ajoute, modifie ou supprime des adresses recommandées aux mamans (nom, catégorie, ville, téléphone). 'Avis signalés' : les avis que les utilisatrices ont signalés comme inappropriés apparaissent ici avec un badge en pastille dans le menu ; tu peux les Valider (garder) ou les Supprimer après confirmation.",
  },
  {
    title: "Utilisatrices",
    body: "Comptes de toutes les mamans inscrites. Cherche par nom ou e-mail. Clique une ligne pour voir la fiche complète. Tu peux Désactiver un compte problématique (la maman ne peut plus se connecter) ou le Réactiver à tout moment. Chaque action demande une confirmation. Le bouton 'Exporter (CSV)' télécharge la liste complète.",
  },
  {
    title: "Rendez-vous",
    body: "Liste de tous les rendez-vous planifiés par les utilisatrices dans l'application. Filtre par période (semaine / mois / trimestre) ou cherche par nom d'utilisatrice. Clique une ligne pour voir le détail (date, heure, lieu, motif, rappel). Clique '+ Nouveau rendez-vous' pour créer un rendez-vous au nom d'une utilisatrice : sélectionne l'utilisatrice, choisis le motif dans la liste, saisis la date et l'heure, le lieu et le délai de rappel. Tu peux aussi Modifier ou Supprimer un rendez-vous existant.",
  },
  {
    title: "Comptes admin",
    body: "Réservé au super-admin. Liste tous les membres de l'équipe ayant accès au back-office. Crée un nouveau compte avec le rôle (Super-admin / Admin / Éditeur / Lecteur) et la matrice de droits par écran (créer, modifier, supprimer). Tu peux Désactiver un compte pour en révoquer l'accès sans le supprimer. Chaque action demande une confirmation.",
  },
  {
    title: "Entreprises · Performance",
    body: "Vue financière et commerciale par entreprise. Pour chaque entreprise, vois la formule d'abonnement souscrite, le montant payé et le nombre d'offres publiées. Cet écran est en lecture seule : il sert à surveiller l'activité commerciale sans modifier les données.",
  },
  {
    title: "Formules & abonnements",
    body: "Catalogue des formules tarifaires proposées aux entreprises. Ajoute une nouvelle formule (nom, prix, durée, limite d'offres), modifie ou supprime une formule existante. La suppression est définitive et demande une confirmation. Ces formules sont celles que les entreprises voient lors de leur inscription.",
  },
  {
    title: "Pays disponibles",
    body: "Liste des pays dans lesquels l'application et le site sont accessibles. Pour chaque pays tu vois le nom, le code ISO, l'indicatif téléphonique et son statut actif/inactif. Ajoute un nouveau pays ou modifie un existant. Désactiver un pays le masque de l'application sans le supprimer.",
  },
  {
    title: "Notifications",
    body: "Modèles de messages automatiques envoyés aux entreprises (confirmation d'inscription, validation d'offre, rappel d'expiration, etc.). Chaque notification a un titre et un corps. Modifie le texte pour adapter le wording sans toucher au code. La suppression d'un modèle est irréversible et demande une confirmation.",
  },
  {
    title: "Catégories articles",
    body: "Référentiel des catégories utilisées pour classer les articles éditoriaux (ex. Santé, Nutrition, Bien-être…). Ajoute, modifie ou supprime une catégorie. Une catégorie supprimée n'est plus disponible lors de la création d'un article. La suppression demande une confirmation.",
  },
  {
    title: "Catégories d'offres",
    body: "Référentiel des catégories affichées dans le fil d'offres de l'application (ex. Soins, Sport, Alimentation…). Ajoute, modifie ou supprime une catégorie. Ces catégories permettent aux mamans de filtrer les offres par thème. La suppression demande une confirmation.",
  },
  {
    title: "Motifs de rendez-vous",
    body: "Liste des motifs proposés lors de la prise de rendez-vous dans l'application mobile (ex. Consultation prénatale, Échographie, Bilan postnatal…). Ajoute un motif, modifie son libellé ou supprime-le. Seuls les motifs actifs apparaissent dans le sélecteur de l'application et dans le formulaire de création de rendez-vous du back-office. La suppression demande une confirmation.",
  },
  {
    title: "Types d'entreprises",
    body: "Référentiel des types d'activité professionnelle (ex. Clinique, Pharmacie, Coach, Sage-femme…). Ces types sont proposés lors de l'inscription d'une entreprise et dans le répertoire. Ajoute, modifie ou supprime un type — la suppression demande une confirmation.",
  },
  {
    title: "Catégories répertoire",
    body: "Référentiel des catégories utilisées pour classer les bonnes adresses dans le répertoire (ex. Santé, Bien-être, Sport…). Ajoute, modifie ou supprime une catégorie. Ces catégories permettent aux mamans de parcourir le répertoire par thème. La suppression demande une confirmation.",
  },
];
